import { useState, useEffect, useCallback, useRef } from 'react';
import { listActivitiesByChapter, listSentencesByChapter, getChapterCustomization, createUserChapterCustomization, createSchoolChapterCustomization, updateChapterCustomization, getUserByEmail } from '@dataconnect/generated';
import { FaExclamationTriangle, FaCog, FaTimes, FaCheck, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import Constants from '../utilities/constants/Constants';
import html2pdf from 'html2pdf.js';
import { getPracticeSheetContentForActivity, getAnswerSheetContentForActivity } from '../utilities/practiceSheetHelper';
import '../App.css';
import activityImg from '../assets/images/activity.png';
import yellowImage from '../assets/images/yellow.png';
import scrollImg from '../assets/images/scroll.png';

const ChapterDetailsPage = ({ selectedChapter, selectedStudent, onLogout, onBack, onViewChapter, onStartActivity, user, isAdmin }) => { // eslint-disable-line no-unused-vars
    const [activities, setActivities] = useState([]);
    const [allActivities, setAllActivities] = useState([]); // All activities for config
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [chapterSentences, setChapterSentences] = useState([]);
    const [chapterCustomization, setChapterCustomization] = useState(null);
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const lastSaveTime = useRef(0);
    
    // Configuration Modal States
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [savingConfig, setSavingConfig] = useState(false);
    const [expandedActivities, setExpandedActivities] = useState({});
    const [localChapterConfig, setLocalChapterConfig] = useState(null); // { activities: [ {activityId, items: []} ] }

    // Fetch customization for this chapter when chapter or student changes
    const fetchCustomization = useCallback(async () => {
        if (!selectedChapter?.id) return;

        try {
            setLoadingConfig(true);
            
            // Get internal user ID if logged in
            let internalUserId = null;
            if (user?.email) {
                const userResult = await getUserByEmail({ email: user.email });
                if (userResult.data?.users?.length) {
                    internalUserId = userResult.data.users[0].id;
                }
            }

            const result = await getChapterCustomization({ 
                userId: internalUserId,
                schoolId: selectedStudent?.school?.id || null,
                chapterId: selectedChapter.id
            }, { fetchPolicy: 'no-cache' });
            
            if (result.data) {
                const isStaleCache = result.source === 'CACHE' && (Date.now() - lastSaveTime.current < 5000);

                if (isStaleCache) {
                    return;
                }
                
                if (result.data.userCustomizations?.length > 1) {
                    console.warn('Multiple user customizations found. Using the first one.');
                }

                // Priority: user customization, then school customization
                const customization = result.data.userCustomizations?.[0] || result.data.schoolCustomizations?.[0];
                
                setChapterCustomization(customization || null);
                // Increment refresh trigger to force activity reload
                setRefreshTrigger(prev => prev + 1);
            }
        } catch (err) {
            console.error('Failed to load chapter customization:', err);
        } finally {
            setLoadingConfig(false);
        }
    }, [selectedChapter?.id, selectedStudent?.school?.id, user?.email]);

    useEffect(() => {
        fetchCustomization();
    }, [fetchCustomization]);

    // Fetch activities from API
    useEffect(() => {
        const fetchActivitiesAndSentences = async () => {
            if (!selectedChapter?.id) return;
            
            // Wait for customization check to finish before fetching activities
            // to avoid double fetching and potential flickers
            if (loadingConfig) {
                return;
            }

            try {
                setLoading(true);
                setError(null);
                
                const response = await listActivitiesByChapter({
                    chapterId: selectedChapter.id
                }, { fetchPolicy: 'no-cache' });

                // Transform API activities to UI format
                const apiActivities = response.data?.activities || [];
                
                const transformedActivities = apiActivities.map(activity => {
                    const mapping = Constants.ACTIVITY_TYPE_CONFIG[activity.activityType] || {
                        title: `${activity.activityType}`,
                        color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        icon: "📝",
                        hoverColor: "#6366f1"
                    };

                    return {
                        ...mapping,
                        id: activity.id,
                        activityType: activity.activityType,
                        baseContent: activity.baseContent,
                        scopeLevel: activity.scopeLevel,
                        scopeId: activity.scopeId
                    };
                });

                // Set all activities for configuration modal
                setAllActivities(transformedActivities);

                // Filter and modify activities based on chapter customization
                let filteredActivities = [...transformedActivities];
                
                if (chapterCustomization?.activityData) {
                    try {
                        const activityDataMap = JSON.parse(chapterCustomization.activityData);
                        
                        // Filter activities based on what's in customization
                        filteredActivities = transformedActivities.filter(activity => {
                            return !!activityDataMap[activity.id];
                        }).map(activity => {
                            // Replace baseContent with filtered content from customization
                            return {
                                ...activity,
                                baseContent: JSON.stringify(activityDataMap[activity.id].content)
                            };
                        });
                    } catch (err) {
                        console.error('Failed to parse customization activity data:', err);
                    }
                }

                // Auto-add Creative Writing activity if chapter type is CREATIVE_WRITING
                const processedActivities = [...filteredActivities];
                if (selectedChapter.type === 'CREATIVE_WRITING') {
                    // Create a synthetic Creative Writing activity
                    const creativeWritingActivity = {
                        ...Constants.ACTIVITY_TYPE_CONFIG.CREATIVE_WRITING,
                        id: 'auto-creative-writing',
                        activityType: 'CREATIVE_WRITING',
                        baseContent: JSON.stringify({
                            prompts: [{
                                title: 'Write about this chapter',
                                description: `Share your thoughts and creativity about "${selectedChapter.title}"`,
                                minWords: 50,
                                maxWords: 200,
                                keywords: [],
                                image: null,
                                rubric: {
                                    creativity: 25,
                                    grammar: 25,
                                    vocabulary: 25,
                                    relevance: 25
                                }
                            }]
                        }),
                        isAutoGenerated: true
                    };
                    processedActivities.push(creativeWritingActivity);
                }

                // Combine Read Chapter with all activities
                // For NUMBER and GRAMMAR chapters, do not enable the Read Chapter activity
                const finalActivities = (selectedChapter.type === 'NUMBERS' || selectedChapter.type === 'GRAMMAR')
                    ? processedActivities
                    : [Constants.ACTIVITY_TYPE_CONFIG.READ_CHAPTER, ...processedActivities];

                setActivities(finalActivities);

                // Also load chapter sentences for creative writing answer sheet
                try {
                    const sentencesRes = await listSentencesByChapter({ chapterId: selectedChapter.id });
                    const items = Array.isArray(sentencesRes?.data?.sentences)
                        ? sentencesRes.data.sentences
                        : [];
                    setChapterSentences(items);
                } catch (sentenceErr) {
                    // If sentences fail to load, just leave chapterSentences empty
                    setChapterSentences([]);
                }
            } catch (err) {

                setError('Failed to load activities');
                // Fallback to just Read Chapter activity (except for NUMBERS and GRAMMAR chapters)
                if (selectedChapter.type === 'NUMBERS' || selectedChapter.type === 'GRAMMAR') {
                    setActivities([]);
                } else {
                    setActivities([Constants.ACTIVITY_TYPE_CONFIG.READ_CHAPTER]);
                }
                setChapterSentences([]);
            } finally {
                setLoading(false);
            }
        };

        fetchActivitiesAndSentences();
    }, [selectedChapter?.id, chapterCustomization, loadingConfig, refreshTrigger]);

    // Parse activity content to get individual items
    const parseActivityItems = (activity) => {
      if (!activity.baseContent) return [];
      try {
        const content = JSON.parse(activity.baseContent);
        const items = [];
        
        switch (activity.activityType) {
          case 'WORD_MEANING':
          case 'WORD_PRACTICE':
          case 'WORD_SPLIT':
          case 'DUAL_MEANING':
            if (content.words && Array.isArray(content.words)) {
              content.words.forEach((w, index) => {
                items.push({
                  id: `word-${index}`,
                  label: w.languageWord || w.word || w.question || w.combinedWord || `Word ${index + 1}`,
                  subtitle: w.englishMeaning || w.meaning || w.answer || ''
                });
              });
            }
            break;
          
          case 'QUESTION_ANSWERS':
          case 'LONG_QUESTION_ANSWERS':
            if (content.questions && Array.isArray(content.questions)) {
              content.questions.forEach((q, index) => {
                // For LONG_QUESTION_ANSWERS, question and answer might be objects
                const qText = typeof q.question === 'object' 
                  ? (q.question.languageText || q.question.englishText || '') 
                  : (q.questionKannada || q.questionHindi || q.question || '');
                
                const aText = typeof q.answer === 'object'
                  ? (Array.isArray(q.answer.sentences) 
                      ? q.answer.sentences.map(s => s.languageText || s.englishText || '').join(' ')
                      : (q.answer.languageText || q.answer.englishText || ''))
                  : (q.answerKannada || q.answerHindi || q.answer || '');

                items.push({
                  id: `question-${index}`,
                  label: qText || `Question ${index + 1}`,
                  subtitle: aText || ''
                });
              });
            }
            break;
          
          case 'WHO_SAID_TO_WHOM': {
            if (content.dialogues && Array.isArray(content.dialogues)) {
              content.dialogues.forEach((item, index) => {
                // item might be a sentence object
                const text = typeof item.text === 'object'
                  ? (item.text.languageText || item.text.englishText || '')
                  : (item.languageText || item.text || '');
                
                items.push({
                  id: `dialogue-${index}`,
                  label: item.whoSaid || item.who || `Dialogue ${index + 1}`,
                  subtitle: text || ''
                });
              });
            }
            break;
          }
          
          case 'MATCH':
          case 'OPPOSITES': {
            const listKey = activity.activityType === 'MATCH' ? 'pairs' : 'words';
            
            if (content[listKey] && Array.isArray(content[listKey])) {
              content[listKey].forEach((item, index) => {
                const idPrefix = activity.activityType === 'MATCH' ? 'pair' : 'opposite';
                items.push({
                  id: `${idPrefix}-${index}`,
                  label: item.left || item.word || `Item ${index + 1}`,
                  subtitle: item.right || item.opposite || ''
                });
              });
            }
            break;
          }

          case 'TENSES':
            if (content.groups && Array.isArray(content.groups)) {
              content.groups.forEach((group, gIndex) => {
                if (group.sentences && Array.isArray(group.sentences)) {
                  group.sentences.forEach((s, sIndex) => {
                    items.push({
                      id: `tense-${gIndex}-${sIndex}`,
                      label: s.languageText || s.englishText || `Sentence ${sIndex + 1}`,
                      subtitle: s.tenseKannada || s.tenseEnglish || ''
                    });
                  });
                }
              });
            }
            break;

          case 'GENDERS':
            if (content.words && Array.isArray(content.words)) {
              content.words.forEach((w, index) => {
                items.push({
                  id: `gender-${index}`,
                  label: w.word || w.englishWord || `Word ${index + 1}`,
                  subtitle: w.genderKannada || w.genderEnglish || ''
                });
              });
            }
            break;

          case 'CHANGE_GENDERS':
            if (content.pairs && Array.isArray(content.pairs)) {
              content.pairs.forEach((p, index) => {
                items.push({
                  id: `change-gender-${index}`,
                  label: `${p.maleLanguageWord} → ${p.femaleLanguageWord}`,
                  subtitle: `${p.maleEnglishWord} → ${p.femaleEnglishWord}`
                });
              });
            }
            break;

          case 'SINGULAR_PLURAL':
            if (content.words && Array.isArray(content.words)) {
              content.words.forEach((w, index) => {
                items.push({
                  id: `singular-plural-${index}`,
                  label: `${w.singularWord} → ${w.pluralWord}`,
                  subtitle: `${w.singularEnglish} → ${w.pluralEnglish}`
                });
              });
            }
            break;

          case 'MAKE_SENTENCES':
            if (content.sentences && Array.isArray(content.sentences)) {
              content.sentences.forEach((s, index) => {
                items.push({
                  id: `sentence-${index}`,
                  label: s.languageWord || `Sentence ${index + 1}`,
                  subtitle: s.languageSentence || ''
                });
              });
            }
            break;

          case 'JOIN_WORDS':
            if (content.words && Array.isArray(content.words)) {
              content.words.forEach((w, index) => {
                items.push({
                  id: `word-${index}`,
                  label: `${w.leftWord} + ${w.rightWord}`,
                  subtitle: w.answer || ''
                });
              });
            }
            break;
          
          default:
            items.push({
              id: 'full-activity',
              label: 'Full Activity',
              subtitle: ''
            });
        }
        
        return items;
      } catch (err) {
        return [{ id: 'full-activity', label: 'Full Activity', subtitle: '' }];
      }
    };

    // Filter activity content based on selected items
    const filterActivityContent = (content, activityType, selectedItems) => {
      if (!selectedItems || selectedItems.length === 0) return content;
      
      const filtered = { ...content };
      
      switch (activityType) {
        case 'WORD_MEANING':
        case 'WORD_PRACTICE':
        case 'WORD_SPLIT':
        case 'DUAL_MEANING':
          if (content.words && Array.isArray(content.words)) {
            filtered.words = content.words.filter((_, index) => selectedItems.includes(`word-${index}`));
          }
          break;
        case 'QUESTION_ANSWERS':
        case 'LONG_QUESTION_ANSWERS':
          if (content.questions && Array.isArray(content.questions)) {
            filtered.questions = content.questions.filter((_, index) => selectedItems.includes(`question-${index}`));
          }
          break;
        case 'MATCH':
          if (content.pairs && Array.isArray(content.pairs)) {
            filtered.pairs = content.pairs.filter((_, index) => selectedItems.includes(`pair-${index}`));
          }
          break;
        case 'OPPOSITES':
          if (content.words && Array.isArray(content.words)) {
            filtered.words = content.words.filter((_, index) => selectedItems.includes(`opposite-${index}`));
          }
          break;
        case 'WHO_SAID_TO_WHOM':
          if (content.dialogues && Array.isArray(content.dialogues)) {
            filtered.dialogues = content.dialogues.filter((_, index) => selectedItems.includes(`dialogue-${index}`));
          }
          break;
        case 'MAKE_SENTENCES':
          if (content.sentences && Array.isArray(content.sentences)) {
            filtered.sentences = content.sentences.filter((_, index) => selectedItems.includes(`sentence-${index}`));
          }
          break;
        case 'JOIN_WORDS':
          if (content.words && Array.isArray(content.words)) {
            filtered.words = content.words.filter((_, index) => selectedItems.includes(`word-${index}`));
          }
          break;
        case 'TENSES':
          if (content.groups && Array.isArray(content.groups)) {
            filtered.groups = content.groups.map((group, gIndex) => ({
              ...group,
              sentences: group.sentences ? group.sentences.filter((_, sIndex) => selectedItems.includes(`tense-${gIndex}-${sIndex}`)) : []
            })).filter(group => group.sentences.length > 0);
          }
          break;
        case 'GENDERS':
          if (content.words && Array.isArray(content.words)) {
            filtered.words = content.words.filter((_, index) => selectedItems.includes(`gender-${index}`));
          }
          break;
        case 'CHANGE_GENDERS':
          if (content.pairs && Array.isArray(content.pairs)) {
            filtered.pairs = content.pairs.filter((_, index) => selectedItems.includes(`change-gender-${index}`));
          }
          break;
        case 'SINGULAR_PLURAL':
          if (content.words && Array.isArray(content.words)) {
            filtered.words = content.words.filter((_, index) => selectedItems.includes(`singular-plural-${index}`));
          }
          break;
      }
      return filtered;
    };

    const computePracticeData = (activityDataMap) => {
      const practiceMap = {};
      Object.entries(activityDataMap).forEach(([activityId, activityInfo]) => {
        const { activityType, content } = activityInfo;
        if (!practiceMap[activityType]) {
          practiceMap[activityType] = { activityType, content: JSON.parse(JSON.stringify(content)) };
        } else {
          const existing = practiceMap[activityType].content;
          // Basic merge logic - add more types as needed
          if (Array.isArray(existing.words) && Array.isArray(content.words)) {
            content.words.forEach(w => {
              if (!existing.words.find(ew => (ew.languageWord || ew.word) === (w.languageWord || w.word))) {
                existing.words.push(w);
              }
            });
          } else if (Array.isArray(existing.questions) && Array.isArray(content.questions)) {
            content.questions.forEach(q => {
              if (!existing.questions.find(eq => (eq.question || eq.questionKannada) === (q.question || q.questionKannada))) {
                existing.questions.push(q);
              }
            });
          }
        }
      });
      return practiceMap;
    };

    const handleOpenConfig = async () => {
      try {
        setLoadingConfig(true);
        // Fetch ALL activities for this chapter
        const response = await listActivitiesByChapter({ chapterId: selectedChapter.id }, { fetchPolicy: 'no-cache' });
        const allActs = response.data?.activities || [];
        setAllActivities(allActs);

        // Parse current customization for this chapter
        if (chapterCustomization) {
          const activityDataMap = JSON.parse(chapterCustomization.activityData || '{}');
          setLocalChapterConfig({
            activities: Object.keys(activityDataMap).map(activityId => ({
              activityId,
              items: activityDataMap[activityId].selectedItems || [] // We'll store selectedItems IDs here
            }))
          });
        } else {
          // Default: All activities selected with all items
          setLocalChapterConfig({
            activities: allActs.map(a => ({
              activityId: a.id,
              items: parseActivityItems(a).map(i => i.id)
            }))
          });
        }
        setShowConfigModal(true);
      } catch (err) {
        console.error('Failed to open config:', err);
        alert('Failed to load activity details for configuration.');
      } finally {
        setLoadingConfig(false);
      }
    };

    const toggleActivitySelection = (activityId) => {
      setLocalChapterConfig(prev => {
        const activities = [...(prev?.activities || [])];
        const index = activities.findIndex(a => a.activityId === activityId);
        
        if (index > -1) {
          // Remove activity
          activities.splice(index, 1);
        } else {
          // Add activity with all items selected
          const act = allActivities.find(a => a.id === activityId);
          const items = parseActivityItems(act).map(i => i.id);
          activities.push({ activityId, items });
        }
        
        return { ...prev, activities };
      });
    };

    const toggleItemSelection = (activityId, itemId) => {
      setLocalChapterConfig(prev => {
        const activities = [...(prev?.activities || [])];
        const actIndex = activities.findIndex(a => a.activityId === activityId);
        
        if (actIndex > -1) {
          const items = [...activities[actIndex].items];
          const itemIndex = items.indexOf(itemId);
          
          if (itemIndex > -1) {
            items.splice(itemIndex, 1);
          } else {
            items.push(itemId);
          }
          
          activities[actIndex] = { ...activities[actIndex], items };
          return { ...prev, activities };
        }
        return prev;
      });
    };

    const saveChapterConfig = async () => {
      try {
        setSavingConfig(true);
        
        // Get internal user ID
        let internalUserId = null;
        if (user?.email) {
            const userResult = await getUserByEmail({ email: user.email });
            if (userResult.data?.users?.length) {
                internalUserId = userResult.data.users[0].id;
            }
        }

        if (!internalUserId && !isAdmin) {
            throw new Error('User identification failed. Please log in again.');
        }

        // 1. Prepare activityDataMap
        const activityDataMap = {};
        localChapterConfig.activities.forEach(actConfig => {
          const activity = allActivities.find(a => a.id === actConfig.activityId);
          if (activity) {
            try {
              const content = JSON.parse(activity.baseContent);
              const filtered = filterActivityContent(content, activity.activityType, actConfig.items);
              activityDataMap[activity.id] = {
                activityType: activity.activityType,
                content: filtered,
                selectedItems: actConfig.items // Store for later editing
              };
            } catch (e) {
              console.error(`Failed to parse content for activity ${activity.id}:`, e);
            }
          }
        });

        // 2. Recompute practice data for this chapter
        const practiceDataMap = computePracticeData(activityDataMap);

        // 3. Save to ChapterCustomization table
        let savedCustomization = null;

        if (isAdmin && selectedStudent?.school?.id) {
            // School-level if admin
            if (chapterCustomization?.id) {
                // Update existing
                await updateChapterCustomization({
                    id: chapterCustomization.id,
                    activityData: JSON.stringify(activityDataMap),
                    practiceData: JSON.stringify(practiceDataMap)
                });
                savedCustomization = {
                    ...chapterCustomization,
                    activityData: JSON.stringify(activityDataMap),
                    practiceData: JSON.stringify(practiceDataMap)
                };
            } else {
                // Create new
                await createSchoolChapterCustomization({
                    schoolId: selectedStudent.school.id,
                    chapterId: selectedChapter.id,
                    activityData: JSON.stringify(activityDataMap),
                    practiceData: JSON.stringify(practiceDataMap)
                });
                // We'll let fetchCustomization get the full object (including ID) for new records
            }
        } else {
            // User-level personal config
            if (chapterCustomization?.id) {
                // Update existing
                await updateChapterCustomization({
                    id: chapterCustomization.id,
                    activityData: JSON.stringify(activityDataMap),
                    practiceData: JSON.stringify(practiceDataMap)
                });
                savedCustomization = {
                    ...chapterCustomization,
                    activityData: JSON.stringify(activityDataMap),
                    practiceData: JSON.stringify(practiceDataMap)
                };
            } else {
                // Create new
                await createUserChapterCustomization({
                    userId: internalUserId,
                    chapterId: selectedChapter.id,
                    activityData: JSON.stringify(activityDataMap),
                    practiceData: JSON.stringify(practiceDataMap)
                });
                // We'll let fetchCustomization get the full object (including ID) for new records
            }
        }

        if (savedCustomization) {
            lastSaveTime.current = Date.now();
            setChapterCustomization(savedCustomization);
            // Increment refresh trigger to force activity reload
            setRefreshTrigger(prev => prev + 1);
            
            alert('Chapter configuration saved successfully!');
            setShowConfigModal(false);
        } else {
            // New record case
            lastSaveTime.current = Date.now();
            alert('Chapter configuration saved successfully!');
            setShowConfigModal(false);
            await fetchCustomization();
        }
      } catch (err) {
        console.error('Failed to save chapter config:', err);
        alert('Failed to save configuration: ' + err.message);
      } finally {
        setSavingConfig(false);
      }
    };

    const handleDownloadPracticeSheet = () => {
        if (!selectedChapter || !activities || activities.length === 0) return;

        const nonReadActivities = activities.filter(a => !a.isReadChapter);
        if (nonReadActivities.length === 0) return;

        const generateFancyPDF = (isAnswerSheet = false) => {
            const element = document.createElement('div');
            // Basic styling for the container
            element.style.fontFamily = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
            element.style.padding = '40px';
            element.style.backgroundColor = '#fff';
            element.style.color = '#1f2937';
            element.style.width = '700px'; // Set a fixed width for consistent rendering before PDF conversion

            // Header Section with Logo placeholder and Chapter info
            let html = `
                <div style="display: flex; justify-content: space-between; align-items: start; border-bottom: 4px solid #1e3a8a; padding-bottom: 25px; margin-bottom: 40px;">
                    <div style="flex: 1;">
                        <div style="font-size: 10pt; color: #6b7280; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 8px;">
                            ZenoKids • Learning Resources
                        </div>
                        <h1 style="color: #1e3a8a; margin: 0; font-size: 26pt; font-weight: 800; line-height: 1.1;">
                            ${selectedChapter.title || 'Chapter Practice'}
                        </h1>
                        <div style="margin-top: 15px; font-size: 12pt; display: flex; gap: 15px; color: #4b5563; font-weight: 600;">
                            <span style="background: #eff6ff; color: #1d4ed8; padding: 3px 10px; border-radius: 6px;">${selectedChapter.subject || ''}</span>
                            <span style="padding: 3px 0;">Grade ${selectedChapter.grade || ''}</span>
                        </div>
                    </div>
                    <div style="text-align: right; margin-left: 20px;">
                        <div style="font-size: 14pt; font-weight: 800; color: #1e3a8a; background: #eff6ff; padding: 10px 20px; border-radius: 12px; border: 2px solid #dbeafe; white-space: nowrap;">
                            ${isAnswerSheet ? 'ANSWER KEY' : 'PRACTICE SHEET'}
                        </div>
                    </div>
                </div>
            `;

            // Activities Section
            nonReadActivities.forEach((activity, idx) => {
                const typeLabel = activity.activityType
                    ? (Constants.ACTIVITY_TYPE_LABELS?.[activity.activityType] || activity.activityType)
                    : 'Activity';
                
                const content = isAnswerSheet 
                    ? getAnswerSheetContentForActivity(activity, selectedChapter?.title, chapterSentences)
                    : getPracticeSheetContentForActivity(activity, selectedChapter?.title);

                html += `
                    <div style="margin-bottom: 35px; page-break-inside: avoid;">
                        <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
                            <div style="min-width: 28px; height: 28px; background: #1e3a8a; color: white; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 11pt;">
                                ${idx + 1}
                            </div>
                            <h3 style="margin: 0; color: #1e3a8a; font-size: 16pt; font-weight: 700;">
                                ${activity.title || typeLabel}
                            </h3>
                        </div>
                        <div style="padding-left: 40px; white-space: pre-wrap; font-size: 12pt; line-height: 1.6; color: #374151; font-weight: 400;">${content.trim()}</div>
                    </div>
                `;
            });

            // Footer Section
            html += `
                <div style="margin-top: 50px; border-top: 1px solid #e5e7eb; padding-top: 20px; text-align: center; color: #9ca3af; font-size: 9pt;">
                    © ${new Date().getFullYear()} ZenoKids • All rights reserved ${selectedStudent ? `• Generated for ${selectedStudent.name}` : ''}
                </div>
            `;

            element.innerHTML = html;
            
            const safeTitle = (selectedChapter.title || 'chapter').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
            const filename = `${safeTitle}_${isAnswerSheet ? 'answer' : 'practice'}_sheet.pdf`;

            const opt = {
                margin: 10,
                filename: filename,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, letterRendering: true },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
            };

            html2pdf().set(opt).from(element).save();
        };

        // Download practice sheet as PDF
        generateFancyPDF(false);

        // Download answer sheet as PDF (with a slight delay to avoid conflicts)
        setTimeout(() => {
            generateFancyPDF(true);
        }, 1200);
    };

    return (
        <div
            className="min-h-screen"
            style={{
                backgroundImage: `url(${yellowImage})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'top center',
                backgroundSize: 'cover'
            }}
        >
            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {selectedChapter && (<>
                        {/* Header Section */}
                        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                            <div>
                                <h1 className="text-4xl font-extrabold text-blue-900 drop-shadow-sm">
                                    {selectedChapter.title}
                                </h1>
                                <p className="text-blue-800 font-medium mt-1">
                                    {selectedChapter.subject} • Grade {selectedChapter.grade}
                                </p>
                            </div>
                            
                            <div className="flex gap-3">
                                {(!isAdmin && !user?.email?.includes('admin') && selectedStudent) && (
                                    <button
                                        onClick={handleOpenConfig}
                                        className="flex items-center px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all font-bold shadow-md"
                                    >
                                        <FaCog className="mr-2" />
                                        Configure Chapter
                                    </button>
                                )}
                                <button
                                    onClick={onBack}
                                    className="px-6 py-2 bg-blue-900 text-white rounded-xl hover:bg-blue-800 transition-all font-bold shadow-md"
                                >
                                    Back
                                </button>
                            </div>
                        </div>

                        {/* Loading State */}
                        {loading && (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
                                    <p className="text-gray-600 text-lg">Loading activities...</p>
                                </div>
                            </div>
                        )}

                        {/* Error State */}
                        {error && !loading && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-8">
                                <div className="flex items-center">
                                    <FaExclamationTriangle className="text-yellow-600 mr-3" />
                                    <span className="text-yellow-800">{error}</span>
                                </div>
                            </div>
                        )}

                        {/* Activity cards */}
                        {!loading && (
                            <div className="grid grid-cols-1 md:grid-cols-6 gap-8">
                                {activities.map((activity, index) => {
                                    const isReadChapterActivity = activity.activityType === Constants.ACTIVITY_TYPES.READ_CHAPTER || activity.activityType === undefined;

                                    // 3 cards per visual row: on md+ use 6 columns and span 2 each.
                                    // Row 0: col-start 1,3,5 ; Row 1: col-start 2,4,6 -> second row sits between first row cards.
                                    const colInRow = index % 3; // 0,1,2
                                    const row = Math.floor(index / 3);
                                    const mdColStart = row % 2 === 0
                                        ? colInRow * 2 + 1 // 1,3,5
                                        : colInRow * 2 + 2; // 2,4,6

                                    return (
                                        <button
                                            key={activity.id || index}
                                            type="button"
                                            onClick={() => {
                                                if (activity.isReadChapter && onViewChapter) {
                                                    onViewChapter(selectedChapter);
                                                } else if (activity.activityType && onStartActivity) {
                                                    onStartActivity(activity);
                                                }
                                            }}
                                            className="w-full flex flex-col items-center cursor-pointer focus:outline-none transform transition-transform duration-300 hover:scale-105"
                                            style={{ gridColumnStart: mdColStart }}
                                        >
                                            <div
                                                className="relative w-52 h-44 mx-auto mb-4 bg-contain bg-center bg-no-repeat flex items-center justify-center px-4"
                                                style={{ backgroundImage: `url(${isReadChapterActivity ? scrollImg : activityImg})` }}
                                            >
                                                <span className="w-full px-6 py-1.5 text-center text-xs sm:text-sm font-extrabold text-amber-50 drop-shadow-[0_1px_1px_rgba(0,0,0,0.9)] tracking-wide uppercase break-words">
                                                    {activity.title}
                                                </span>
                                            </div>
                                            {activity.description && (
                                                <p className="mt-2 text-xs text-slate-100/85 max-w-sm sm:max-w-md text-center px-6 break-words">
                                                    {activity.description}
                                                </p>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Download Practice Sheet Button */}
                        {!loading && activities && activities.filter(a => !a.isReadChapter).length > 0 && (
                            <div className="mt-10 flex justify-center">
                                <button
                                    type="button"
                                    onClick={handleDownloadPracticeSheet}
                                    className="px-6 py-3 rounded-xl bg-indigo-600 text-white font-semibold shadow-lg hover:bg-indigo-700 transition-colors"
                                >
                                    Download Practice Sheet
                                </button>
                            </div>
                        )}

                        {/* Configuration Modal */}
                        {showConfigModal && (
                            <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                                    <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-blue-50 rounded-t-2xl">
                                        <div>
                                            <h2 className="text-2xl font-bold text-blue-900">Configure Activities</h2>
                                            <p className="text-blue-700 text-sm">Select activities and items for {selectedChapter.title}</p>
                                        </div>
                                        <button onClick={() => setShowConfigModal(false)} className="text-gray-400 hover:text-gray-600">
                                            <FaTimes size={24} />
                                        </button>
                                    </div>

                                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                        {allActivities.map((activity) => {
                                            const actConfig = localChapterConfig?.activities?.find(a => a.activityId === activity.id);
                                            const isSelected = !!actConfig;
                                            const items = parseActivityItems(activity);
                                            const isExpanded = expandedActivities[activity.id];

                                            return (
                                                <div key={activity.id} className="border border-gray-200 rounded-xl overflow-hidden">
                                                    <div className={`p-4 flex items-center justify-between ${isSelected ? 'bg-blue-50/50' : 'bg-gray-50'}`}>
                                                        <div className="flex items-center gap-3">
                                                            <button 
                                                                onClick={() => toggleActivitySelection(activity.id)}
                                                                className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                                                                    isSelected ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 bg-white'
                                                                }`}
                                                            >
                                                                {isSelected && <FaCheck size={12} />}
                                                            </button>
                                                            <div>
                                                                <h4 className="font-bold text-gray-900">
                                                                    {Constants.ACTIVITY_TYPE_LABELS?.[activity.activityType] || activity.activityType}
                                                                </h4>
                                                                <p className="text-xs text-gray-500">{items.length} items available</p>
                                                            </div>
                                                        </div>
                                                        
                                                        {isSelected && items.length > 0 && (
                                                            <button 
                                                                onClick={() => setExpandedActivities(prev => ({ ...prev, [activity.id]: !prev[activity.id] }))}
                                                                className="flex items-center text-blue-600 font-medium text-sm hover:underline"
                                                            >
                                                                {isExpanded ? <><FaChevronDown className="mr-1" /> Hide Items</> : <><FaChevronRight className="mr-1" /> Configure Items</>}
                                                            </button>
                                                        )}
                                                    </div>

                                                    {isSelected && isExpanded && items.length > 0 && (
                                                        <div className="p-4 bg-white border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-3">
                                                            {items.map(item => {
                                                                const isItemSelected = actConfig.items.includes(item.id);
                                                                return (
                                                                    <div 
                                                                        key={item.id} 
                                                                        onClick={() => toggleItemSelection(activity.id, item.id)}
                                                                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all flex items-start gap-3 ${
                                                                            isItemSelected ? 'border-blue-200 bg-blue-50' : 'border-gray-100 hover:border-gray-200'
                                                                        }`}
                                                                    >
                                                                        <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center ${
                                                                            isItemSelected ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 bg-white'
                                                                        }`}>
                                                                            {isItemSelected && <FaCheck size={8} />}
                                                                        </div>
                                                                        <div>
                                                                            <div className="text-sm font-bold text-gray-800 line-clamp-1">{item.label}</div>
                                                                            {item.subtitle && <div className="text-xs text-gray-500 line-clamp-1">{item.subtitle}</div>}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div className="p-6 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
                                        <button 
                                            onClick={() => setShowConfigModal(false)}
                                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-100 transition-all"
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            onClick={saveChapterConfig}
                                            disabled={savingConfig}
                                            className="px-8 py-2 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50"
                                        >
                                            {savingConfig ? 'Saving...' : 'Save Configuration'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ChapterDetailsPage;
